import type { EChartsOption } from 'echarts';
import type { LivePositionItem } from '../../services/graphApi';

function getPositionRangeLabel(position: LivePositionItem, nextPosition: LivePositionItem | undefined) {
  if (position.splitId == null) {
    return position.splitName;
  }

  return nextPosition ? `${position.splitName}~${nextPosition.splitName}` : `${position.splitName}~`;
}

function getLabel(
  position: LivePositionItem,
  nextPosition: LivePositionItem | undefined,
  showCategoryName: boolean
) {
  const rangeLabel = getPositionRangeLabel(position, nextPosition);
  return showCategoryName ? `${position.categoryName} / ${rangeLabel}` : rangeLabel;
}

export function createLivePositionOption(
  positions: LivePositionItem[],
  showCategoryName: boolean
): EChartsOption {
  const labels = positions.map((position, index) => getLabel(position, positions[index + 1], showCategoryName));
  const values = positions.map((position) => position.participantCount);
  const maxValue = Math.max(...values, 0);

  return {
    backgroundColor: '#ffffff',
    animation: false,
    color: ['#2670d8'],
    grid: {
      top: 34,
      left: 52,
      right: 28,
      bottom: 72,
      containLabel: true,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      formatter: (params) => {
        const item = Array.isArray(params) ? params[0] : params;
        const dataIndex = Number((item as { dataIndex?: number }).dataIndex ?? 0);
        const position = positions[dataIndex];

        if (!position) {
          return '';
        }

        return [
          `<strong>${getPositionRangeLabel(position, positions[dataIndex + 1])}</strong>`,
          `종목: ${position.categoryName}`,
          `참가자 수: ${position.participantCount}명`,
        ].join('<br />');
      },
    },
    xAxis: {
      type: 'category',
      data: labels,
      axisTick: {
        show: false,
      },
      axisLabel: {
        color: '#64748b',
        fontSize: 11,
        interval: 0,
        rotate: labels.some((label) => label.length > 10) ? 32 : 0,
      },
      axisLine: {
        lineStyle: {
          color: '#e5e7eb',
        },
      },
    },
    yAxis: {
      type: 'value',
      name: 'participants',
      min: 0,
      max: Math.max(Math.ceil(maxValue / 10) * 10, 10),
      minInterval: 1,
      axisLabel: {
        color: '#64748b',
        fontSize: 11,
      },
      splitLine: {
        lineStyle: {
          color: '#edf2f7',
        },
      },
    },
    series: [
      {
        name: '참가자 수',
        type: 'bar',
        barMaxWidth: 42,
        itemStyle: {
          color: '#2670d8',
          borderRadius: [4, 4, 0, 0],
        },
        label: {
          show: true,
          position: 'top',
          color: '#111827',
          fontSize: 11,
          fontWeight: 800,
        },
        data: values,
      },
    ],
  };
}
